/**
 * Path: src/App.jsx
 */
import React, { useState, useEffect, useCallback, lazy, Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from "@sentry/react";

import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { NotificationProvider } from './context/NotificationContext.jsx';
import { CONFIG } from './lib/config.js';

import Layout from './components/layout/Layout.jsx';
import LoginScreen from './components/preAuth/LoginScreen.jsx';
import SignupScreen from './components/preAuth/SignupScreen.jsx';
import VerifyEmailScreen from './components/preAuth/VerifyEmailScreen.jsx';
import InitialTours from './components/tours/InitialTours.jsx';

import AcceptTerms from './components/legal/AcceptTerms.jsx';
import TermsOfService from './components/legal/TermsOfService.jsx';
import PrivacyPolicy from './components/legal/PrivacyPolicy.jsx';

const lazyWithRetry = (componentImport) =>
    lazy(async () => {
        const pageHasBeenRefreshed = JSON.parse(
            sessionStorage.getItem('page_has_been_refreshed') || 'false'
        );
        try {
            const component = await componentImport();
            sessionStorage.setItem('page_has_been_refreshed', 'false');
            return component;
        } catch (error) {
            if (!pageHasBeenRefreshed) {
                sessionStorage.setItem('page_has_been_refreshed', 'true');
                window.location.reload();
                return { default: () => <></> };
            }
            throw error;
        }
    });

const AdminSuite = lazyWithRetry(() => import('./components/admin/AdminSuite.jsx'));
const BuildingScreen = lazyWithRetry(() => import('./components/preAuth/BuildingScreen.jsx'));
const OverviewDashboard = lazyWithRetry(() => import('./components/overview/OverviewDashboard.jsx'));
const DataConnectorManager = lazyWithRetry(() => import('./components/dataConnectors/DataConnectorManager.jsx'));
const RawTables = lazyWithRetry(() => import('./components/rawTables/RawTables.jsx'));
const DimensionsManager = lazyWithRetry(() => import('./components/dimensions/DimensionsManager.jsx'));
const PipelinesManager = lazyWithRetry(() => import('./components/pipelines/PipelinesManager.jsx'));
const WarehouseManager = lazyWithRetry(() => import('./components/warehouse/WarehouseManager.jsx'));
const ReportsManager = lazyWithRetry(() => import('./components/reports/ReportsManager.jsx'));
const UserProfile = lazyWithRetry(() => import('./components/settings/UserProfile.jsx'));
const TenantSettings = lazyWithRetry(() => import('./components/settings/TenantSettings.jsx'));
const InsightsManager = lazyWithRetry(() => import('./components/insights/InsightsManager.jsx'));

class ErrorBoundary extends Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Navigation or chunk error caught:", error, errorInfo);
        Sentry.captureException(error, { extra: errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#0a0a0a] text-white">
                    <h2 className="text-lg font-bold mb-2">Navigation Error</h2>
                    <p className="text-xs text-gray-400 mb-4">A temporary network or bundle loading issue occurred.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#3160F4] hover:opacity-90 transition-opacity"
                    >
                        Reload Workspace
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const queryClient = new QueryClient();

const PageFallback = ({ isDark = true }) => (
    <div className={`min-h-screen flex items-center justify-center transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F5F5F7]'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3160F4]"></div>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isProvisioning, authLoading } = useAuth();
    if (authLoading) return <PageFallback isDark={true} />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (isProvisioning) return <Navigate to="/provisioning" replace />;
    return children;
};

const ProvisioningRoute = ({ children }) => {
    const { isProvisioning, isAuthenticated, authLoading } = useAuth();
    if (authLoading) return <PageFallback isDark={true} />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!isProvisioning) return <Navigate to="/" replace />;
    return children;
};

const RBACRoute = ({ sectionName, children }) => {
    const { hasSectionAccess } = useAuth();
    if (!hasSectionAccess(sectionName)) {
        return <Navigate to="/" replace />;
    }
    return children;
};

function AppRouter() {
    const { user, userEmail, updateUser, fetchWithAuth, checkProvisioningStatus, completeProvisioning } = useAuth();
    const location = useLocation();

    const [isDark, setIsDark] = useState(() => {
        const savedLocalTheme = localStorage.getItem('theme_preference');
        if (savedLocalTheme === 'light') return false;
        return true;
    });

    useEffect(() => {
        if (user && user.theme) {
            const preferDark = user.theme !== 'light';
            setIsDark(preferDark);
            localStorage.setItem('theme_preference', preferDark ? 'dark' : 'light');
        }
    }, [user]);

    const handleToggleTheme = useCallback(async () => {
        const newIsDark = !isDark;
        setIsDark(newIsDark);
        const themeStr = newIsDark ? 'dark' : 'light';
        localStorage.setItem('theme_preference', themeStr);

        if (user && userEmail) {
            updateUser({ theme: themeStr });
            try {
                await fetchWithAuth(`${CONFIG.API_BASE_URL}/tenant/user/theme`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: userEmail, theme: themeStr })
                });
            } catch (err) {
                console.error("Failed to persist theme preference to server:", err);
            }
        }
    }, [isDark, user, userEmail, updateUser, fetchWithAuth]);

    return (
        <Suspense fallback={<PageFallback isDark={isDark} />}>
            <Routes>
                <Route path="/login" element={<LoginScreen isDark={isDark} toggleTheme={handleToggleTheme} />} />
                <Route path="/signup" element={<SignupScreen isDark={isDark} toggleTheme={handleToggleTheme} />} />
                <Route path="/verify" element={<VerifyEmailScreen isDark={isDark} />} />

                <Route path="/accept-terms" element={<AcceptTerms isDark={isDark} />} />
                <Route path="/terms" element={<TermsOfService isDark={isDark} />} />
                <Route path="/privacy" element={<PrivacyPolicy isDark={isDark} />} />

                {/* AdminSuite receives standard toggleTheme */}
                <Route path="/admin" element={<AdminSuite isDark={isDark} toggleTheme={handleToggleTheme} />} />

                <Route path="/provisioning" element={
                    <ProvisioningRoute>
                        <BuildingScreen
                            isDark={isDark}
                            onCheckStatus={checkProvisioningStatus}
                            onComplete={completeProvisioning}
                        />
                    </ProvisioningRoute>
                } />

                <Route path="/" element={
                    <ProtectedRoute>
                        <InitialTours isDark={isDark} />
                        <Layout isDark={isDark} setIsDark={setIsDark} />
                    </ProtectedRoute>
                }>
                    <Route index element={<OverviewDashboard isDark={isDark} />} />
                    <Route path="data-connectors" element={<RBACRoute sectionName="data-connectors"><DataConnectorManager isDark={isDark} /></RBACRoute>} />
                    <Route path="raw-tables" element={<RBACRoute sectionName="raw-tables"><RawTables isDark={isDark} /></RBACRoute>} />
                    <Route path="dimensions" element={<RBACRoute sectionName="dimensions"><DimensionsManager isDark={isDark} /></RBACRoute>} />
                    <Route path="pipelines" element={<RBACRoute sectionName="pipelines"><PipelinesManager isDark={isDark} /></RBACRoute>} />
                    <Route path="warehouse" element={<RBACRoute sectionName="warehouse"><WarehouseManager isDark={isDark} /></RBACRoute>} />
                    <Route path="reports" element={<RBACRoute sectionName="reports"><ReportsManager isDark={isDark} /></RBACRoute>} />
                    <Route path="insights" element={<RBACRoute sectionName="insights"><InsightsManager isDark={isDark} /></RBACRoute>} />
                    <Route path="profile" element={<UserProfile isDark={isDark} toggleTheme={handleToggleTheme} />} />
                    <Route path="settings" element={<RBACRoute sectionName="settings"><TenantSettings isDark={isDark} /></RBACRoute>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <NotificationProvider>
                    <ErrorBoundary>
                        <BrowserRouter>
                            <AppRouter />
                        </BrowserRouter>
                    </ErrorBoundary>
                </NotificationProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
}